/* ============================================================
   API ↔ UI mappers for the YouTube Auto-Dub feature.
   The backend speaks snake_case (FastAPI); the components were
   built against the mock shapes in mockData.js. Everything that
   crosses that boundary is normalized here — the components stay
   byte-identical.
   ============================================================ */
import moment from 'moment';
import { STAGES, STATUS } from './mockData.js';

/* ---- deterministic gradient per channel/video (no artwork from the API
   placeholder thumbs keep the prototype look) ---- */
export const GRADS = [
	['#ff3a44', '#b30019'],
	['#3b6ef5', '#1733c4'],
	['#ff9f43', '#e8590c'],
	['#16c098', '#0a8f6c'],
	['#7c3aed', '#5b21b6'],
	['#f6a609', '#d97706'],
	['#2f6cf6', '#1e54d6'],
	['#10b981', '#047857'],
	['#0ea5e9', '#0369a1'],
	['#ec4899', '#9d174d'],
	['#6366f1', '#4338ca'],
	['#fb7185', '#be123c'],
	['#ff6b9d', '#c2185b'],
	['#14b8a6', '#0f766e'],
];

function djb2(str) {
	let h = 5381;
	for (let i = 0; i < str.length; i++) h = (((h << 5) + h) + str.charCodeAt(i)) >>> 0;
	return h;
}

export const gradFor = (seed) => GRADS[djb2(String(seed ?? '')) % GRADS.length];

/* '03:06' — zero-padded minutes (the queue rows use clock-style durations,
   unlike cost.js fmtDuration which renders '3:06') */
export function fmtClock(sec) {
	const s = Math.max(0, Math.round(sec || 0));
	return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
}

/* '4 min ago' — moment.fromNow() squeezed to the prototype's short units */
export function timeAgoShort(iso) {
	if (!iso) return '';
	return moment(iso).fromNow()
		.replace(' minutes', ' min').replace(' minute', ' min')
		.replace(' seconds', ' sec').replace(' second', ' sec')
		.replace(' hours', ' hr').replace(' hour', ' hr');
}

const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

/* ---- channels ---- */
export function normalizeChannel(api) {
	return {
		id: String(api.id),
		name: api.channel_title || 'YouTube channel',
		handle: api.channel_handle || '—',
		subs: '—', // the YouTube list call doesn't return subscriber counts
		grad: gradFor(api.platform_channel_id || api.id),
		provider: api.platform || 'youtube',
		connected: api.status === 'connected',
		connectedAt: api.created_at ? moment(api.created_at).format('MMM DD, YYYY') : '',
		thumbnail: api.thumbnail,
		platform_channel_id: api.platform_channel_id,
		status: api.status,
		needs_reconnect: !!api.needs_reconnect,
	};
}

/* ---- source-library videos ---- */
export function normalizeVideo(api) {
	return {
		id: String(api.id),
		title: api.title,
		dur: api.duration_seconds ?? 0, // number — cost.js videoCost reads v.dur
		views: api.view_count ?? 0,
		ago: api.published_at ? moment(api.published_at).fromNow() : '',
		date: api.published_at ? moment(api.published_at).format('YYYY-MM-DD') : '',
		type: api.video_type === 'short' ? 'short' : 'long',
		playlists: api.playlists ?? [],
		playlist: (api.playlists ?? [])[0] ?? '',
		tags: (api.tags ?? []).map(t => t.toLowerCase()),
		grad: gradFor(api.id),
		thumbnail: api.thumbnail,
	};
}

/* ---- workflows ---- */
export function normalizeWorkflow(api) {
	const mapping = api.mapping ?? {};
	return {
		id: String(api.id),
		name: api.name,
		type: api.type,
		status: STATUS[api.status] ? api.status : 'paused',
		paused_reason: api.paused_reason ?? null,
		source: String(api.source_channel_id),
		dests: api.publish_mode === 'audio_track'
			? [String(api.source_channel_id)]
			: [...new Set(Object.values(mapping).flat())].map(String),
		langs: api.languages ?? [],
		publishMode: api.publish_mode,
		review: !!api.review,
		privacy: api.privacy,
		stats: {
			detected: api.stats?.detected ?? 0,
			dubbing: api.stats?.dubbing ?? 0,
			review: api.stats?.review ?? 0,
			published: api.stats?.published ?? 0,
			failed: api.stats?.failed ?? 0,
			queue: api.stats?.queue ?? 0,
		},
		creditsMonth: api.credits_used_this_month ?? 0,
		capMonth: api.monthly_credit_cap || 200000,
		created: api.created_at ? moment(api.created_at).format('MMM DD, YYYY') : '',
		voice: api.voice?.mode === 'clone'
			? 'clone'
			: (Object.values(api.voice?.voice_by_lang ?? {})[0] ?? 'clone'),
		mapping,
	};
}

/* ---- queue items ---- */
export function normalizeQueueItem(api, wf) {
	// QueueItem indexes STAGES[it.stage] directly — never let an unknown stage
	// through. The backend's 'skipped' renders as a failed row with its note.
	let stage = api.stage === 'skipped' ? 'failed' : api.stage;
	if (!STAGES[stage]) stage = 'detected';
	return {
		id: String(api.id),
		projectId: api.project_id != null ? String(api.project_id) : null,
		title: api.title,
		dur: fmtClock(api.duration_seconds),
		grad: gradFor(api.id),
		detected: timeAgoShort(api.detected_at),
		stage,
		progress: Number(api.progress ?? 0),
		lang: api.language ?? api.language_code,
		dest: api.dest_channel_id != null ? String(api.dest_channel_id) : '',
		cost: Math.round(api.estimated_cost ?? 0),
		mode: api.publish_mode ?? wf?.publishMode,
		publishedVideoId: api.published_video_id || null,
		publishedAt: api.published_at
			? `${capitalize(wf?.privacy ?? 'public')} · ${timeAgoShort(api.published_at)}`
			: undefined,
		error: api.error ?? (api.note || null),
	};
}

/* ---- builder draft → POST /workflows payload ---- */
export function draftToWorkflowPayload(d) {
	const f = d.filters || {};
	return {
		name: d.name || `${d.source?.name} auto-dub`,
		type: d.type,
		source_channel_id: Number(d.source?.id),
		languages: d.langs,
		publish_mode: d.publishMode,
		mapping: d.publishMode === 'audio_track'
			? {}
			: Object.fromEntries(d.langs.map(c => [c, (d.mapping[c] || []).map(Number)])),
		review: !!d.review,
		privacy: d.publish.privacy,
		translate_metadata: !!d.publish.translateMeta,
		copy_tags: !!d.publish.copyTags,
		// Voice is now picked per language (no clone/fixed mode). The backend
		// resolves voice_by_lang[language] → SpeakerMaster id; mode stays 'fixed'.
		voice: {
			mode: 'fixed',
			clone_voice: null,
			voice_by_lang: Object.fromEntries(
				d.langs
					.filter(c => d.voiceByLang?.[c])
					.map(c => [c, d.voiceByLang[c]])
			),
		},
		monthly_credit_cap: Number(d.capMonth) || 0,
		// Backfill only: the explicit per-video pick from Step 2. Omitted for watch
		// (rule-based) and when no selection was computed (backend queues all matched).
		...(d.type === 'backfill' && Array.isArray(d.selectedIds)
			? { selected_video_ids: d.selectedIds }
			: {}),
		filters: {
			title_keywords: f.titleKeywords || [],
			title_mode: f.titleMode || 'any',
			tags: f.tags || [],
			playlists: f.playlists || [],
			date_from: f.dateFrom || null,
			date_to: f.dateTo || null,
			min_views: f.minViews ? Number(f.minViews) : null,
			duration_min_minutes: f.durMin ? Number(f.durMin) : null,
			duration_max_minutes: f.durMax ? Number(f.durMax) : null,
			shorts: f.shorts || 'include',
		},
	};
}

/* ---- raw GET /workflows/:id → builder draft (inverse of the above) ----
   Used by the edit flow to pre-populate the 4-step builder. Fed the RAW api
   workflow (not normalizeWorkflow), plus the normalized channels list so the
   source can be resolved to a full channel object. */
export function workflowToDraft(api, channels = []) {
	const f = api.filters || {};
	const voice = api.voice || {};
	const mapping = api.mapping || {};
	const numOrEmpty = (v) => (v === null || v === undefined ? '' : String(v));
	// Restore the watch-mode "all vs filtered" toggle: any populated rule means the
	// automation was saved as 'filtered'; an empty filter set means "dub everything".
	const hasFilter = !!(
		(f.title_keywords || []).length || (f.tags || []).length || (f.playlists || []).length
		|| f.date_from || f.date_to || f.min_views != null
		|| f.duration_min_minutes != null || f.duration_max_minutes != null
		|| (f.shorts && f.shorts !== 'include')
	);
	return {
		type: api.type,
		source: channels.find(c => c.id === String(api.source_channel_id)) || null,
		filterMode: hasFilter ? 'filtered' : 'all',
		filters: {
			titleKeywords: f.title_keywords || [],
			titleMode: f.title_mode || 'any',
			tags: f.tags || [],
			playlists: f.playlists || [],
			dateFrom: f.date_from || '',
			dateTo: f.date_to || '',
			minViews: numOrEmpty(f.min_views),
			durMin: numOrEmpty(f.duration_min_minutes),
			durMax: numOrEmpty(f.duration_max_minutes),
			shorts: f.shorts || 'include',
		},
		langs: api.languages || [],
		publishMode: api.publish_mode || 'new_video',
		// channel ids are strings in the UI; the API stores them as ints
		mapping: Object.fromEntries(
			Object.entries(mapping).map(([code, ids]) => [code, (ids || []).map(String)])
		),
		// Per-language voice. Legacy 'clone' workflows stored a single voice id on
		// voice.clone_voice — fan it out across every language so the picker shows
		// it. A legacy 'auto' clone has no concrete voice, so it lands empty and
		// the editor must pick one.
		voiceByLang: voice.mode === 'clone' && voice.clone_voice && voice.clone_voice !== 'auto'
			? Object.fromEntries((api.languages || []).map(c => [c, voice.clone_voice]))
			: (voice.voice_by_lang || {}),
		// Display details for the saved voice ids (keyed by id), so each picker
		// card renders the chosen voice immediately instead of "Voice selected ·
		// open to view". Resolved server-side in _workflow_out.voice_meta.
		voiceMeta: api.voice_meta || {},
		publish: {
			privacy: api.privacy || 'public',
			translateMeta: api.translate_metadata !== false,
			copyTags: api.copy_tags !== false,
		},
		review: api.review !== false,
		capMonth: api.monthly_credit_cap || 200000,
		name: api.name || '',
		// Videos already in the automation (backfill edit). [{source_video_id, stage,
		// removable}] — drives the picker's pre-selection, lock state and badges.
		existingVideos: (api.existing_videos || []).map(v => ({
			id: String(v.source_video_id),
			stage: v.stage,
			removable: !!v.removable,
		})),
		// Seed the inclusion selection with everything already queued, so the picker
		// opens pre-selected. The user then adds/removes from here.
		selectedIds: (api.existing_videos || []).map(v => String(v.source_video_id)),
	};
}

/* Human-readable messages for the ?channel_connect=error&reason=... values
   the backend appends when the OAuth round-trip fails. */
export const CONNECT_ERRORS = {
	access_denied: 'You cancelled the Google permission request.',
	invalid_state: 'The connection link expired. Please try again.',
	token_exchange_failed: "Couldn't complete sign-in with Google. Please try again.",
	channel_fetch_failed: "Couldn't read your YouTube channel. Make sure the account has a channel.",
	missing_params: 'Something went wrong during sign-in. Please try again.',
};
